import { Button, Grid, IconButton, InputLabel, Link, Modal, TextField, useTheme } from "@mui/material";
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import PeopleOutlineRoundedIcon from '@mui/icons-material/PeopleOutlineRounded';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useState } from "react";
import { request } from "@/Api";
import { CustomBox } from "@/common/StyledComponents";
import ShortcutOutlinedIcon from '@mui/icons-material/ShortcutOutlined';
import ControlPointRoundedIcon from '@mui/icons-material/ControlPointRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsSuperAdmin } from "@/auth/authSlice";

const shortcutIcons = {
    "Project Folder": FolderOpenRoundedIcon,
    "Resourcing": PeopleOutlineRoundedIcon,
    "Contracts": NoteAltOutlinedIcon,
    "Timeline": ViewTimelineOutlinedIcon,
    "Completion Report": WorkspacePremiumOutlinedIcon,
}

export default function ProjectShortcuts({ shortcuts, onShortcutUpdate, onShortcutCreate, onShortcutDelete }) {
    const [ selectedShortcut, setSelectedShortcut ] = useState(null)
    const handleShortcutPopupOpen = (shortcut) => {
        setSelectedShortcut(shortcut)
    }
    const handleShortcutChange = (shortcut) => {
        setSelectedShortcut(shortcut)
    }
    const handleShortcutUpdate = (shortcut) => {
        onShortcutUpdate && onShortcutUpdate(shortcut)
    }
    const handleShortcutCreate = (shortcut) => {
        onShortcutCreate && onShortcutCreate(shortcut)
    }
    const handleShortcutDelete = (shortcut) => {   
        onShortcutDelete && onShortcutDelete(shortcut)
    }
    return (
    <>
        <Grid container spacing={2}>
            {shortcuts.map((shortcut, index) => <>
                <ShortcutGridItem 
                    shortcut={shortcut} 
                    index={index} 
                    onShortcutPopupOpen={handleShortcutPopupOpen}
                />
            </>)}
        </Grid>
        <Button startIcon={<ControlPointRoundedIcon />} sx={{marginTop: 3}} onClick={() => handleShortcutPopupOpen({name: "", url: ""})}>
            Add
        </Button>
        {selectedShortcut &&
        <ShortcutPopup 
            open={selectedShortcut !== null}
            shortcut={selectedShortcut}
            handleClose={() => setSelectedShortcut(null)}
            onShortcutChange={handleShortcutChange}
            onShortcutUpdate={handleShortcutUpdate}
            onShortcutCreate={handleShortcutCreate}
            onShortcutDelete={handleShortcutDelete}
        />}
    </>
)}

const ShortcutGridItem = ({ shortcut, index, onShortcutPopupOpen }) => {
    const theme = useTheme()
    const Icon = shortcutIcons[shortcut.name] || ArticleOutlinedIcon;
    const handleEditButton = () => {
        onShortcutPopupOpen && onShortcutPopupOpen(shortcut)
    }
    const isPlaceholder = shortcut.shortcut_type == "placeholder"
    const isSuperAdmin = useSelector(selectIsSuperAdmin)
    const readonly = shortcut.shortcut_type == "readonly" && !isSuperAdmin

    return (
        <Grid item key={index} md={4}>
            <Grid item>
                <Grid container alignItems="center" sx={{background: theme.palette.background.paper, padding: 2, borderRadius: 2}} justifyContent={"space-between"}>
                    <Grid item>
                        <Grid container alignItems="center">
                            <Grid item>
                                <Icon />
                            </Grid>
                            <Grid item>
                                <div style={{lineHeight: 1.2, paddingLeft: 5}}>
                                    {shortcut.url ? <Link target="_blank" href={shortcut.url}>{shortcut.name}</Link> : <span>{shortcut.name}</span>}
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container>
                            {(isPlaceholder && !shortcut.url) &&
                                <Grid item>
                                    <IconButton sx={{pointerEvents: "none"}}>
                                        <LinkOffOutlinedIcon />
                                    </IconButton>
                                </Grid>
                            }
                            <Grid item>
                                <IconButton disabled={readonly} size="small" onClick={handleEditButton}>
                                    {readonly ? <LockOutlinedIcon /> : <EditOutlinedIcon />}
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}

const ShortcutPopup = ({shortcut, open, handleClose, onShortcutUpdate, onShortcutCreate, onShortcutChange, onShortcutDelete}) => {
    const { projectId } = useParams()
    const theme = useTheme()
    const isCreate = shortcut.id == null
    const isPlaceholder = shortcut.shortcut_type == "placeholder"
    const handleShortcutChange = (field) => (event) => {
        onShortcutChange && onShortcutChange({...shortcut, [field]: event.target.value})
    }
    const handleEditShortcut = () => {
        const url = shortcut.url.startsWith("http") ? shortcut.url : `https://${shortcut.url}`
        request.put(`/shortcuts/${shortcut.id}`, {...shortcut, url})
        .then((response) => {
            if (response.status === 200) {
                onShortcutUpdate && onShortcutUpdate(response.data)
                handleClose()
            }
        })
    }
    const handleCreateShortcut = () => {
        const url = shortcut.url.startsWith("http") ? shortcut.url : `https://${shortcut.url}`
        request.post("/shortcuts", {...shortcut, project_id: projectId, url})
        .then((response) => {
            if (response.status === 200) {
                onShortcutCreate && onShortcutCreate(response.data)
                handleClose()
            }
        })
    }
    const handleDeleteShortcut = () => {
        request.delete(`/shortcuts/${shortcut.id}`)
        .then((response) => {
            if (response.status === 200) {
                onShortcutDelete && onShortcutDelete(response.data)
                handleClose()
            }
        })
    }
    return (
        <Modal
            sx={{overflow: "scroll"}}
            open={open}
            onClose={handleClose}
        >
            <CustomBox sx={{backgroundColor: theme.palette.background.default, padding: 5}}>
                <Grid container alignItems={"center"} sx={{marginBottom: 3}}>
                    <Grid item>
                        <div style={{background: theme.palette.background.paper, padding: 3, paddingBottom: 0, borderRadius: 5, marginRight: 10}}>
                            <ShortcutOutlinedIcon color="success"/>
                        </div>
                    </Grid>
                    <Grid item><span>{`${isCreate ? "Create" : "Edit"} Shortcut`}</span></Grid>
                </Grid>
                <InputLabel>Name</InputLabel>
                <TextField
                    disabled={isPlaceholder}
                    fullWidth
                    value={shortcut.name}
                    onChange={handleShortcutChange("name")}
                    sx={{marginBottom: 3}}
                />
                <InputLabel>URL</InputLabel>
                <TextField
                    fullWidth
                    value={shortcut.url}
                    onChange={handleShortcutChange("url")}
                />
                <Grid container justifyContent={"right"} sx={{marginTop: 3, width: "100%"}}>
                    <Grid item sx={{marginRight: 2}}>
                        <Button onClick={handleClose} variant="contained" color="secondary">
                            Cancel
                        </Button>
                    </Grid>
                    {!isCreate &&
                    <Grid item sx={{marginRight: 2}}>
                        <Button onClick={handleDeleteShortcut} variant="contained" color="error">
                            Delete Shortcut
                        </Button>
                    </Grid>}
                    <Grid item>
                        <Button onClick={isCreate ? handleCreateShortcut : handleEditShortcut} variant="contained">
                            {isCreate ? "Create" : "Update"} Shortcut
                        </Button>
                    </Grid>
                </Grid>
            </CustomBox>
        </Modal>
    )
}